/** Batch "Reimport" dialog for the import-runs grid (a custom `dialog(ctx)`
 *  batch action) — the console counterpart of the dashboard's bulk reimport.
 *
 *  Each selected run's stored input is re-submitted as a NEW import run
 *  (origin Console, owned by each run's own tenant) and dispatched through
 *  the normal import pipeline. One optional input: whether the re-imports
 *  send guest notifications.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useState } from 'react';

import type { BatchActionContext } from '@/components/DataGrid/batchTypes';

export function ReimportBatchDialog({ ctx }: { ctx: BatchActionContext }) {
  const [sendNotifications, setSendNotifications] = useState(false);

  const submit = () => {
    void ctx.runStandard({ send_notifications: sendNotifications });
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Reimport</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="warning">
            Each selected run&apos;s stored input is re-imported as a new run and re-enters the
            normal import pipeline — capped at 1,000 rows per batch. Runs in the background.
          </Alert>

          <FormControlLabel
            control={
              <Switch
                checked={sendNotifications}
                onChange={(e) => setSendNotifications(e.target.checked)}
              />
            }
            label="Send notifications for changes made by the reimport"
          />

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">
              Acting on {ctx.matchedPhrase} import runs (up to the
              1,000-row cap).
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={submit}>
          Reimport {ctx.count.toLocaleString()} run(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
